// WordPress-compatible wrapper
(function() {
    'use strict';

// Add debugging at the top
console.log("Interactive Map Script Starting...");
console.log("mapConfig available:", typeof window.mapConfig !== "undefined");
console.log("Map provider:", window.mapConfig?.provider || 'not set');

class InteractiveMap {
    constructor(mapElement, options = {}) {
        const mapId = mapElement.id || 'unknown';
        console.log("✅ InteractiveMap constructor called for:", mapId);
        console.log("mapElement:", mapElement);
        console.log("options:", options);

        this.mapElement = mapElement;
        this.mapId = mapId; // Store for logging
        
        // CRITICAL: Prevent map container from being focusable or scrolling into view
        mapElement.setAttribute('tabindex', '-1');
        mapElement.style.outline = 'none';
        
        this.map = null;
        this.mapProvider = null; // Map provider abstraction
        this.markers = [];
        this.infoWindow = null;
        this.currentView = "country";
        this.currentRegion = null;
        this.locations = [];
        this.userLocation = null;
        this.isNavigating = false; // Flag to prevent zoom listener interference
        this.userClickedStore = false; // Track if user clicked on a specific store

        // New options for single location mode
        this.mode = options.mode || 'full'; // 'full' or 'single'
        this.focusLocationId = options.focusLocationId || null;
        this.showControls = options.showControls !== false;
        this.allowZoomOut = options.allowZoomOut !== false;

        // Device detection
        this.isMobile = window.matchMedia('(max-width: 480px)').matches;
        this.isTablet = window.matchMedia('(min-width: 481px) and (max-width: 768px)').matches;
        this.mobileSheetOpen = false;
        this.tabletDrawerOpen = false;

        // Region boundary functionality
        this.regionBoundaries = new Map(); // Store region boundary data
        this.currentRegionBoundary = null; // Currently displayed boundary

        // Updated coordinates for full New Zealand view
        this.defaultCenter = options.center || { lat: -40.9006, lng: 174.8860 }; // Center of NZ
        this.defaultZoom = options.zoom || 5; // Reduced zoom to show whole country
        this.regionZoomThreshold = 8;

        // Custom region order - used as fallback when no user location
        this.regionOrder = [
            'warkworth', 'warkworth region',
            'hibiscus / rodney', 'hibiscus coast', 'hibiscus coast / rodney', 'rodney',
            'north shore', 'north shore region',
            'west auckland', 'west auckland region',
            'central auckland', 'central auckland region',
            'east auckland', 'east auckland region',
            'south auckland', 'south auckland region',
            'hamilton / waikato', 'hamilton', 'waikato',
            'tauranga / papamoa', 'tauranga', 'papamoa',
            'rotorua', 'rotorua region',
            'palmerston north', 'palmerston north region',
            'kapiti coast / johnsonville', 'kapiti coast', 'johnsonville',
            'wellington', 'wellington region',
            'nelson / tasman', 'nelson', 'tasman', 'tasman bay', 'tasman bay / nelson',
            'christchurch', 'christchurch region'
        ];

        this.init();
    }

    async init() {
        console.log("🚀 Initializing map...");
        
        // Store reference on DOM element for inline handlers (info window buttons)
        this.mapElement._mapInstance = this;

        // Show loading state
        this.showLoadingState();

        if (typeof window.mapConfig === "undefined") {
            console.error("❌ mapConfig not found! Check if PHP code is working.");
            this.showError("Map configuration not found. Please contact support.");
            return;
        }

        try {
            // Create the appropriate map provider
            const provider = window.mapConfig.provider || 'google';
            console.log("🏭 Using map provider:", provider);
            
            if (typeof window.createMapProvider === 'undefined') {
                throw new Error('Map provider library not loaded. Please refresh the page.');
            }
            
            this.mapProvider = window.createMapProvider(provider);

            await this.loadLocations();
            console.log("✅ Locations loaded:", this.locations);

            if (this.locations.length === 0) {
                this.showError("No locations found. Please add locations in the WordPress admin.");
                return;
            }

            await this.initMap();
            console.log("✅ Map", this.mapId, "initialized");

            // Load region boundaries
            await this.loadRegionBoundaries();
            console.log("✅ Region boundaries loaded");

            // Only populate sidebar in full mode
            if (this.mode === 'full') {
                this.populateSidebar();
                console.log("✅ Sidebar populated");

                // Set up responsive panels (mobile sheet / tablet drawer)
                this.setupResponsivePanels();
            }

            if (this.showControls) {
                this.addControls();
            }

            this.addZoomListener();
            console.log("✅ Controls added");

            // Handle single location focus
            if (this.mode === 'single' && this.focusLocationId) {
                this.focusOnSingleLocation();

                // On mobile, add "tap to interact" overlay so the page can scroll
                if (this.isMobile) {
                    this.setupTapToInteract();
                }
            }

            // Hide loading state
            this.hideLoadingState();

        } catch (error) {
            console.error("❌ Error during initialization:", error);
            this.showError(`Failed to initialize map: ${error.message}`);
        }
    }

    showLoadingState() {
        const loadingHTML = `
            <div class="map-loading-overlay" id="map-loading-overlay">
                <div class="map-loading-spinner">
                    <svg width="50" height="50" viewBox="0 0 50 50">
                        <circle cx="25" cy="25" r="20" fill="none" stroke="#FECC20" stroke-width="4" stroke-dasharray="31.4 31.4" stroke-linecap="round">
                            <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite"/>
                        </circle>
                    </svg>
                    <p>Loading map...</p>
                </div>
            </div>
        `;
        this.mapElement.insertAdjacentHTML('beforeend', loadingHTML);
    }

    hideLoadingState() {
        const overlay = document.getElementById('map-loading-overlay');
        if (overlay) {
            overlay.style.opacity = '0';
            setTimeout(() => overlay.remove(), 300);
        }
    }

    showError(message) {
        this.hideLoadingState();
        
        const errorHTML = `
            <div class="map-error-overlay">
                <div class="map-error-content">
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <h3>Map Error</h3>
                    <p>${message}</p>
                    <button onclick="location.reload()" class="retry-button">Retry</button>
                </div>
            </div>
        `;
        this.mapElement.innerHTML = errorHTML;
    }

    async loadRegionBoundaries() {
        console.log("🗺️ Loading region boundaries...");

        // Sample New Zealand region boundaries (approximate)
        // You can replace these with more accurate GeoJSON data
        const boundaries = {
            'auckland': {
                type: "Polygon",
                coordinates: [[
                    [174.2, -37.2],
                    [175.4, -37.2],
                    [175.4, -36.0],
                    [174.2, -36.0],
                    [174.2, -37.2]
                ]]
            },
            'wellington': {
                type: "Polygon",
                coordinates: [[
                    [174.5, -41.8],
                    [175.5, -41.8],
                    [175.5, -40.8],
                    [174.5, -40.8],
                    [174.5, -41.8]
                ]]
            },
            'canterbury': {
                type: "Polygon",
                coordinates: [[
                    [170.0, -44.5],
                    [173.5, -44.5],
                    [173.5, -42.5],
                    [170.0, -42.5],
                    [170.0, -44.5]
                ]]
            },
            'otago': {
                type: "Polygon",
                coordinates: [[
                    [168.0, -46.5],
                    [171.5, -46.5],
                    [171.5, -44.0],
                    [168.0, -44.0],
                    [168.0, -46.5]
                ]]
            }
        };

        // Store boundaries
        Object.entries(boundaries).forEach(([regionName, boundary]) => {
            this.regionBoundaries.set(regionName, boundary);
        });

        // Try to load boundaries from location data if available
        const regionLocations = this.locations.filter(loc => loc.type === 'region');
        regionLocations.forEach(region => {
            if (region.boundary_data) {
                try {
                    const boundaryData = JSON.parse(region.boundary_data);
                    const regionKey = region.title.toLowerCase().replace(/\s+/g, '');
                    this.regionBoundaries.set(regionKey, boundaryData);
                    console.log(`✅ Loaded boundary for ${region.title}`);
                } catch (error) {
                    console.error("Invalid boundary data for:", region.title);
                }
            }
        });
    }

    focusOnSingleLocation() {
        const location = this.locations.find(loc => loc.id == this.focusLocationId);
        if (!location) {
            console.error("❌ Location not found:", this.focusLocationId);
            console.log("Available location IDs:", this.locations.map(l => l.id));
            return;
        }

        console.log("✅ Focusing on single location:", location.title, location.type);

        // If it's a store or mobile store, show its region view and then focus on it with popup
        if ((location.type === 'store' || location.type === 'mobile_store') && location.parent_region) {
            console.log("📍 Store detected, showing region:", location.parent_region);
            this.showRegionView(location.parent_region);
            
            // Wait for region markers to be created, then focus on the store
            setTimeout(() => {
                console.log("🎯 Region loaded, markers created:", this.markers.length);
                // Find the marker for this store
                const marker = this.markers.find(m => m.locationId === location.id);
                if (marker) {
                    console.log("✅ Found marker for store, animating and opening popup");
                    this.animateAndShowStore(location, marker);
                } else {
                    console.error("❌ Marker not found for store:", location.id, "Available:", this.markers.map(m => m.locationId));
                }
            }, 800); // Slightly longer timeout to ensure markers are created
        } else if (location.type === 'region') {
            console.log("🗺️ Region detected, showing region view");
            this.showRegionView(location.id);
            this.updateControlButtons();
        } else if (location.type === 'store' || location.type === 'mobile_store') {
            // Store without parent region - add marker and zoom to it
            console.log("📍 Store without region, adding marker and zooming");
            this.addMarker(location, location.type);
            setTimeout(() => {
                const marker = this.markers.find(m => m.locationId === location.id);
                if (marker) {
                    this.animateAndShowStore(location, marker);
                }
            }, 500);
        }
    }

    populateSidebar() {
        const sidebar = document.getElementById("map-sidebar");
        if (!sidebar) {
            console.error("❌ Sidebar element not found!");
            return;
        }

        // Create header with search and geolocation
        const headerHTML = `
            <div class="sidebar-header">
                <h3>Regions</h3>
                <div class="sidebar-controls">
                    <button id="find-nearest-btn" class="find-nearest-btn" title="Find nearest store">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        <span>Find Nearest</span>
                    </button>
                </div>
                <div class="sidebar-search">
                    <input type="text" id="location-search" placeholder="Search stores or regions..." />
                    <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                    </svg>
                </div>
            </div>
        `;

        sidebar.innerHTML = headerHTML;

        // Get only regions for the sidebar
        const locationsToShow = this.locations.filter(
            (loc) => loc.type === "region"
        );

        // Build search index: for each region, collect child store/mobile names + addresses
        this.buildRegionSearchIndex();

        const listContainer = document.createElement("div");
        listContainer.className = "sidebar-locations-list";
        listContainer.id = "sidebar-locations-list";

        // Sort regions using custom North-to-South order
        this.sortRegionsByCustomOrder(locationsToShow);

        locationsToShow.forEach((location) => {
            const item = this.createSidebarItem(location);
            listContainer.appendChild(item);
        });

        sidebar.appendChild(listContainer);

        // Add event listeners
        this.setupSidebarInteractions();
        
        // Try to get user location and sort by distance
        this.tryAutoSortByDistance();
    }

    /**
     * Try to automatically get user location and sort by distance
     */
    tryAutoSortByDistance() {
        if (!navigator.geolocation) {
            console.log("📍 Geolocation not supported");
            return;
        }
        
        // Try to get cached position first (fast, no prompt)
        navigator.geolocation.getCurrentPosition(
            (position) => {
                console.log("📍 Got user location automatically");
                this.userLocation = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
                this.sortSidebarByDistance(true); // Show message on locations page
            },
            (error) => {
                // User denied or error - keep custom order, no problem
                console.log("📍 Could not get auto location:", error.message);
            },
            {
                enableHighAccuracy: false,
                timeout: 5000,
                maximumAge: 300000 // Use cached position up to 5 minutes old
            }
        );
    }
    
    /**
     * Sort regions by custom North-to-South order
     */
    sortRegionsByCustomOrder(regions) {
        regions.sort((a, b) => {
            const aTitle = a.title.toLowerCase().replace(/\s*region\s*$/i, '').trim();
            const bTitle = b.title.toLowerCase().replace(/\s*region\s*$/i, '').trim();
            
            // Find index in custom order
            let aIndex = this.regionOrder.findIndex(r => 
                aTitle.includes(r) || r.includes(aTitle)
            );
            let bIndex = this.regionOrder.findIndex(r => 
                bTitle.includes(r) || r.includes(bTitle)
            );
            
            // If not found in custom order, put at end alphabetically
            if (aIndex === -1) aIndex = 999;
            if (bIndex === -1) bIndex = 999;
            
            // If both are not in custom order, sort alphabetically
            if (aIndex === 999 && bIndex === 999) {
                return a.title.localeCompare(b.title);
            }
            
            return aIndex - bIndex;
        });
        
        console.log("📋 Regions sorted (North to South):", regions.map(r => r.title));
    }
    
    /**
     * Build a search index mapping each region ID to its child store/mobile names and addresses.
     * Stored in this.regionSearchIndex as a Map: regionId -> { keywords: string, children: [{title, address}] }
     */
    buildRegionSearchIndex() {
        this.regionSearchIndex = new Map();

        const regions = this.locations.filter(loc => loc.type === 'region');
        const childLocations = this.locations.filter(loc => loc.type === 'store' || loc.type === 'mobile_store');

        regions.forEach(region => {
            const children = childLocations.filter(loc => loc.parent_region == region.id);
            const parts = [region.title.toLowerCase()];

            children.forEach(child => {
                if (child.title) parts.push(child.title.toLowerCase());
                if (child.address) parts.push(child.address.toLowerCase());
                if (child.area) parts.push(child.area.toLowerCase());
            });

            // Also include the region's own area field
            if (region.area) parts.push(region.area.toLowerCase());

            this.regionSearchIndex.set(region.id, {
                keywords: parts.join(' | '),
                children: children.map(c => ({
                    title: c.title,
                    address: c.address || '',
                    area: c.area || ''
                }))
            });
        });

        console.log("🔍 Search index built for", this.regionSearchIndex.size, "regions");
    }

    createSidebarItem(location) {
        const item = document.createElement("div");
        item.className = "sidebar-location-item";
        item.dataset.locationId = location.id;
        item.dataset.locationName = location.title.toLowerCase();

        // Store full search keywords from the index
        const indexEntry = this.regionSearchIndex?.get(location.id);
        if (indexEntry) {
            item.dataset.searchKeywords = indexEntry.keywords;
        }

        const distanceHTML = location.distance ? 
            `<div class="location-distance">${location.distance.toFixed(1)} km away</div>` : '';

        // Region icon
        const icon = `
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
                <line x1="8" y1="2" x2="8" y2="18"></line>
                <line x1="16" y1="6" x2="16" y2="22"></line>
            </svg>
        `;

        // Remove "Region" from the display title
        const displayTitle = location.title.replace(/\s*Region\s*$/i, '').trim();

        item.innerHTML = `
            <div class="icon">
                ${icon}
            </div>
            <div class="location-details">
                <div class="title">${displayTitle}</div>
                ${distanceHTML}
                <div class="search-match-hint" style="display:none;"></div>
            </div>
        `;

        item.addEventListener("click", () => {
            console.log("📋 Sidebar item clicked:", location.title, "Type:", location.type);

            // Auto-close panel on mobile/tablet before navigating
            if (this.isMobile && this.mobileSheetOpen) {
                this.closeMobileSheet();
            } else if (this.isTablet && this.tabletDrawerOpen) {
                this.closeTabletDrawer();
            }

            this.showRegionView(location.id);
            this.updateControlButtons();
        });

        return item;
    }

    setupSidebarInteractions() {
        // Search functionality
        const searchInput = document.getElementById("location-search");
        if (searchInput) {
            searchInput.addEventListener("input", (e) => {
                this.filterLocations(e.target.value);
            });
        }

        // Find nearest functionality
        const findNearestBtn = document.getElementById("find-nearest-btn");
        if (findNearestBtn) {
            findNearestBtn.addEventListener("click", () => {
                this.findNearestStore();
            });
        }
    }

    // =====================================================================
    // Responsive Panels — reactive to viewport changes
    // Manages: mobile bottom sheet, tablet left drawer, desktop (no panel)
    // =====================================================================

    setupResponsivePanels() {
        // Track which panel is currently active: 'mobile' | 'tablet' | null
        this.activePanel = null;

        // Run once for current state
        this.updateResponsivePanel();

        // Listen for breakpoint changes
        this.mqMobile = window.matchMedia('(max-width: 480px)');
        this.mqTablet = window.matchMedia('(min-width: 481px) and (max-width: 768px)');

        this.mqMobile.addEventListener('change', () => this.updateResponsivePanel());
        this.mqTablet.addEventListener('change', () => this.updateResponsivePanel());
    }

    updateResponsivePanel() {
        this.isMobile = window.matchMedia('(max-width: 480px)').matches;
        this.isTablet = window.matchMedia('(min-width: 481px) and (max-width: 768px)').matches;

        if (this.isMobile && this.activePanel !== 'mobile') {
            // Tear down tablet if active, set up mobile
            if (this.activePanel === 'tablet') this.teardownTabletDrawer();
            this.setupMobileBottomSheet();
            this.activePanel = 'mobile';
            console.log("✅ Switched to mobile bottom sheet");
        } else if (this.isTablet && this.activePanel !== 'tablet') {
            // Tear down mobile if active, set up tablet
            if (this.activePanel === 'mobile') this.teardownMobileBottomSheet();
            this.setupTabletDrawer();
            this.activePanel = 'tablet';
            console.log("✅ Switched to tablet drawer");
        } else if (!this.isMobile && !this.isTablet && this.activePanel !== null) {
            // Desktop: tear down whichever is active
            if (this.activePanel === 'mobile') this.teardownMobileBottomSheet();
            if (this.activePanel === 'tablet') this.teardownTabletDrawer();
            this.activePanel = null;
            console.log("✅ Switched to desktop (no panel)");
        }
    }

    // ----- Mobile Bottom Sheet -----

    setupMobileBottomSheet() {
        if (this.mobileToggleBtn) return; // Already set up

        const container = document.getElementById('map-layout-container');
        const sidebar = document.getElementById('map-sidebar');
        if (!container || !sidebar) return;

        this.mobileBackdrop = document.createElement('div');
        this.mobileBackdrop.className = 'mobile-sheet-backdrop';
        container.appendChild(this.mobileBackdrop);

        this.mobileToggleBtn = document.createElement('button');
        this.mobileToggleBtn.className = 'mobile-sheet-toggle';
        this.mobileToggleBtn.setAttribute('aria-label', 'Browse regions');
        this.mobileToggleBtn.innerHTML = `
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
                <line x1="8" y1="2" x2="8" y2="18"></line>
                <line x1="16" y1="6" x2="16" y2="22"></line>
            </svg>
            <span>Browse Regions</span>
        `;
        container.appendChild(this.mobileToggleBtn);

        this.mobileToggleBtn.addEventListener('click', () => this.openMobileSheet());
        this.mobileBackdrop.addEventListener('click', () => this.closeMobileSheet());
    }

    teardownMobileBottomSheet() {
        this.closeMobileSheet();
        if (this.mobileToggleBtn) { this.mobileToggleBtn.remove(); this.mobileToggleBtn = null; }
        if (this.mobileBackdrop) { this.mobileBackdrop.remove(); this.mobileBackdrop = null; }
    }

    openMobileSheet() {
        const sidebar = document.getElementById('map-sidebar');
        if (!sidebar) return;

        sidebar.classList.add('mobile-sheet-open');
        if (this.mobileBackdrop) this.mobileBackdrop.classList.add('visible');
        if (this.mobileToggleBtn) this.mobileToggleBtn.classList.add('hidden');
        this.mobileSheetOpen = true;
    }

    closeMobileSheet() {
        const sidebar = document.getElementById('map-sidebar');
        if (!sidebar) return;

        sidebar.classList.remove('mobile-sheet-open');
        if (this.mobileBackdrop) this.mobileBackdrop.classList.remove('visible');
        if (this.mobileToggleBtn) this.mobileToggleBtn.classList.remove('hidden');
        this.mobileSheetOpen = false;
    }

    // ----- Tablet Left-Side Drawer -----

    setupTabletDrawer() {
        if (this.tabletToggleBtn) return; // Already set up

        const container = document.getElementById('map-layout-container');
        const sidebar = document.getElementById('map-sidebar');
        if (!container || !sidebar) return;

        this.tabletBackdrop = document.createElement('div');
        this.tabletBackdrop.className = 'tablet-drawer-backdrop';
        container.appendChild(this.tabletBackdrop);

        this.tabletToggleBtn = document.createElement('button');
        this.tabletToggleBtn.className = 'tablet-drawer-toggle';
        this.tabletToggleBtn.setAttribute('aria-label', 'Toggle regions panel');
        this.tabletToggleBtn.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
                <line x1="8" y1="2" x2="8" y2="18"></line>
                <line x1="16" y1="6" x2="16" y2="22"></line>
            </svg>
            <span>Regions</span>
        `;
        container.appendChild(this.tabletToggleBtn);

        this.tabletToggleBtn.addEventListener('click', () => {
            if (this.tabletDrawerOpen) {
                this.closeTabletDrawer();
            } else {
                this.openTabletDrawer();
            }
        });

        this.tabletBackdrop.addEventListener('click', () => this.closeTabletDrawer());
    }

    teardownTabletDrawer() {
        this.closeTabletDrawer();
        if (this.tabletToggleBtn) { this.tabletToggleBtn.remove(); this.tabletToggleBtn = null; }
        if (this.tabletBackdrop) { this.tabletBackdrop.remove(); this.tabletBackdrop = null; }
    }

    openTabletDrawer() {
        const sidebar = document.getElementById('map-sidebar');
        if (!sidebar) return;

        sidebar.classList.add('tablet-drawer-open');
        if (this.tabletBackdrop) this.tabletBackdrop.classList.add('visible');
        if (this.tabletToggleBtn) this.tabletToggleBtn.classList.add('drawer-open');
        this.tabletDrawerOpen = true;
    }

    closeTabletDrawer() {
        const sidebar = document.getElementById('map-sidebar');
        if (!sidebar) return;

        sidebar.classList.remove('tablet-drawer-open');
        if (this.tabletBackdrop) this.tabletBackdrop.classList.remove('visible');
        if (this.tabletToggleBtn) this.tabletToggleBtn.classList.remove('drawer-open');
        this.tabletDrawerOpen = false;
    }

    // =====================================================================
    // Mobile Single Map: Tap to Interact
    // Prevents the map from stealing scroll on mobile embedded maps.
    // Shows a translucent overlay; tap enables map gestures, timeout re-locks.
    // =====================================================================

    setupTapToInteract() {
        // Disable map touch gestures initially
        this.mapProvider.disableTouchGestures();
        this.mapTouchActive = false;

        // Create overlay
        this.tapOverlay = document.createElement('div');
        this.tapOverlay.className = 'map-tap-overlay';
        this.tapOverlay.innerHTML = `
            <div class="map-tap-overlay-content">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2"/>
                    <path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2"/>
                    <path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8"/>
                    <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 13"/>
                </svg>
                <span>Tap to interact with map</span>
            </div>
        `;
        this.mapElement.style.position = 'relative';
        this.mapElement.appendChild(this.tapOverlay);

        // Tap on overlay → enable gestures
        this.tapOverlay.addEventListener('click', () => {
            this.enableMapTouch();
        });

        // Re-lock when user scrolls the page (meaning they left the map)
        this._scrollLockHandler = () => {
            if (this.mapTouchActive) {
                this.disableMapTouch();
            }
        };
        window.addEventListener('scroll', this._scrollLockHandler, { passive: true });
    }

    enableMapTouch() {
        if (!this.tapOverlay) return;
        this.mapProvider.enableTouchGestures();
        this.tapOverlay.classList.add('hidden');
        this.mapTouchActive = true;

        // Auto re-lock after 8 seconds of inactivity
        clearTimeout(this._touchTimeout);
        this._touchTimeout = setTimeout(() => {
            this.disableMapTouch();
        }, 8000);

        // Reset timeout on any map interaction
        const resetTimeout = () => {
            clearTimeout(this._touchTimeout);
            this._touchTimeout = setTimeout(() => {
                this.disableMapTouch();
            }, 8000);
        };
        this.mapElement.addEventListener('touchmove', resetTimeout, { passive: true });
    }

    disableMapTouch() {
        if (!this.tapOverlay) return;
        this.mapProvider.disableTouchGestures();
        this.tapOverlay.classList.remove('hidden');
        this.mapTouchActive = false;
        clearTimeout(this._touchTimeout);
    }

    filterLocations(searchTerm) {
        const listContainer = document.getElementById("sidebar-locations-list");
        if (!listContainer) return;

        const items = listContainer.querySelectorAll(".sidebar-location-item");
        const term = searchTerm.toLowerCase().trim();

        items.forEach(item => {
            const hintEl = item.querySelector('.search-match-hint');

            // No search term: show all, clear hints
            if (!term) {
                item.style.display = "flex";
                if (hintEl) {
                    hintEl.style.display = 'none';
                    hintEl.textContent = '';
                }
                return;
            }

            const regionName = item.dataset.locationName || '';
            const keywords = item.dataset.searchKeywords || regionName;
            const regionId = item.dataset.locationId;

            // Check if the term matches anywhere in the keywords
            if (keywords.includes(term)) {
                item.style.display = "flex";

                // If region name itself matches, no hint needed
                if (regionName.includes(term)) {
                    if (hintEl) {
                        hintEl.style.display = 'none';
                        hintEl.textContent = '';
                    }
                } else if (hintEl && this.regionSearchIndex) {
                    // Term matched a child store/address/area — build a hint
                    const indexEntry = this.regionSearchIndex.get(parseInt(regionId) || regionId);
                    if (indexEntry) {
                        const matchingChildren = indexEntry.children.filter(child => {
                            const childText = (child.title + ' ' + child.address + ' ' + (child.area || '')).toLowerCase();
                            return childText.includes(term);
                        });

                        if (matchingChildren.length > 0) {
                            // Show up to 2 matching names, then "+N more"
                            // Prefer area name over title for display (e.g. "Takapuna" instead of "Mobile Service Available")
                            const maxShow = 2;
                            const names = matchingChildren.slice(0, maxShow).map(c => {
                                // Use area if it matches the search, otherwise use title
                                if (c.area && c.area.toLowerCase().includes(term)) {
                                    return c.area;
                                }
                                return c.title;
                            });
                            const remaining = matchingChildren.length - maxShow;
                            let hintText = names.join(', ');
                            if (remaining > 0) {
                                hintText += ` +${remaining} more`;
                            }
                            hintEl.textContent = hintText;
                            hintEl.style.display = 'block';
                        }
                    }
                }
            } else {
                item.style.display = "none";
                if (hintEl) {
                    hintEl.style.display = 'none';
                    hintEl.textContent = '';
                }
            }
        });
    }

    async findNearestStore() {
        const btn = document.getElementById("find-nearest-btn");
        
        if (!navigator.geolocation) {
            alert("Geolocation is not supported by your browser");
            return;
        }

        // Close panel on mobile/tablet so the map is visible
        if (this.isMobile && this.mobileSheetOpen) {
            this.closeMobileSheet();
        } else if (this.isTablet && this.tabletDrawerOpen) {
            this.closeTabletDrawer();
        }

        // Show loading state
        btn.disabled = true;
        btn.innerHTML = `
            <svg class="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10" opacity="0.25"></circle>
                <path d="M12 2 A10 10 0 0 1 22 12" opacity="0.75"></path>
            </svg>
            <span>Finding...</span>
        `;

        try {
            const position = await this.getUserLocation();
            this.userLocation = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
            };

            // Calculate distances to all stores (excluding mobile stores)
            const storeLocations = this.locations.filter(loc => loc.type === "store");
            const storesWithDistance = storeLocations.map(store => {
                const distance = this.calculateDistance(
                    this.userLocation.lat,
                    this.userLocation.lng,
                    store.lat,
                    store.lng
                );
                return { ...store, distance };
            });

            // Sort by distance
            storesWithDistance.sort((a, b) => a.distance - b.distance);

            // Update sidebar with distances
            this.updateSidebarWithDistances(storesWithDistance);

            // Focus on nearest store
            if (storesWithDistance.length > 0) {
                const nearest = storesWithDistance[0];
                this.focusOnStore(nearest);
                
                // Show success message
                this.showTemporaryMessage(`📍 Nearest store: ${nearest.title} (${nearest.distance.toFixed(1)} km away)`);
            }

        } catch (error) {
            console.error("Error getting location:", error);
            alert("❌ Could not get your location. Please enable location services and try again.");
        } finally {
            // Reset button
            btn.disabled = false;
            btn.innerHTML = `
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span>Find Nearest</span>
            `;
        }
    }

    getUserLocation() {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            });
        });
    }

    calculateDistance(lat1, lon1, lat2, lon2) {
        // Haversine formula to calculate distance between two points
        const R = 6371; // Radius of the Earth in kilometers
        const dLat = this.toRadians(lat2 - lat1);
        const dLon = this.toRadians(lon2 - lon1);
        
        const a = 
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;
        
        return distance;
    }

    toRadians(degrees) {
        return degrees * (Math.PI / 180);
    }

    /**
     * Sort sidebar by distance to user (called when user location is available)
     * @param {boolean} showMessage - Whether to show notification message (default: true)
     */
    sortSidebarByDistance(showMessage = true) {
        if (!this.userLocation) return;
        
        const listContainer = document.getElementById("sidebar-locations-list");
        if (!listContainer) return;
        
        console.log("📍 Sorting sidebar by distance to user location");
        
        // Get regions and calculate distance to each region's center
        const regions = this.locations.filter(loc => loc.type === 'region');
        
        const regionsWithDistance = regions.map(region => {
            const distance = this.calculateDistance(
                this.userLocation.lat,
                this.userLocation.lng,
                parseFloat(region.lat),
                parseFloat(region.lng)
            );
            return { ...region, distance };
        });
        
        // Sort by distance (closest first)
        regionsWithDistance.sort((a, b) => a.distance - b.distance);
        
        console.log("📋 Regions sorted by distance:", regionsWithDistance.map(r => `${r.title} (${r.distance.toFixed(1)}km)`));
        
        // Rebuild sidebar with distance info
        listContainer.innerHTML = '';
        regionsWithDistance.forEach(location => {
            const item = this.createSidebarItem(location);
            listContainer.appendChild(item);
        });
        
        // Show message only on the locations page
        if (showMessage && window.location.pathname.includes('/locations')) {
            this.showTemporaryMessage("📍 Regions sorted by distance to you");
        }
    }
    
    updateSidebarWithDistances(storesWithDistance) {
        // After finding nearest, we still show regions in sidebar
        // but we could highlight the region containing the nearest store
        const listContainer = document.getElementById("sidebar-locations-list");
        if (!listContainer) return;

        // Get regions and sort by whether they contain nearby stores
        const regions = this.locations.filter(loc => loc.type === 'region');
        
        // Find which regions have stores and calculate min distance to region
        const regionsWithDistance = regions.map(region => {
            const storesInRegion = storesWithDistance.filter(s => s.parent_region == region.id);
            const minDistance = storesInRegion.length > 0 
                ? Math.min(...storesInRegion.map(s => s.distance))
                : Infinity;
            return { ...region, distance: minDistance === Infinity ? null : minDistance };
        });

        // Sort by distance (regions with nearby stores first)
        regionsWithDistance.sort((a, b) => {
            if (a.distance === null && b.distance === null) return a.title.localeCompare(b.title);
            if (a.distance === null) return 1;
            if (b.distance === null) return -1;
            return a.distance - b.distance;
        });

        listContainer.innerHTML = '';
        regionsWithDistance.forEach(location => {
            const item = this.createSidebarItem(location);
            listContainer.appendChild(item);
        });
    }

    showTemporaryMessage(message) {
        // Create message element
        const msgEl = document.createElement("div");
        msgEl.className = "map-temporary-message";
        msgEl.textContent = message;
        msgEl.style.cssText = `
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: #fecc20;
            color: #00355b;
            padding: 12px 24px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
            z-index: 10000;
            font-size: 14px;
            font-weight: 600;
            animation: slideDown 0.3s ease;
        `;

        document.body.appendChild(msgEl);

        // Remove after 4 seconds
        setTimeout(() => {
            msgEl.style.animation = "slideUp 0.3s ease";
            setTimeout(() => msgEl.remove(), 300);
        }, 4000);
    }

    setActiveSidebarItem(locationId) {
        if (this.mode !== 'full') return;

        const sidebar = document.getElementById("map-sidebar");
        if (!sidebar) return;

        sidebar
            .querySelectorAll(".sidebar-location-item")
            .forEach((el) => el.classList.remove("active"));

        const activeItem = sidebar.querySelector(
            `.sidebar-location-item[data-location-id='${locationId}']`
        );
        if (activeItem) {
            activeItem.classList.add("active");
        }
    }



    async loadLocations() {
        const url = `${window.mapConfig.ajaxUrl}?action=get_map_locations`;
        console.log("📡 Loading locations from:", url);

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'same-origin'
            });
            
            console.log("📡 AJAX response status:", response.status);

            if (!response.ok) {
                throw new Error(`Failed to load locations: HTTP ${response.status}`);
            }

            const data = await response.json();
            console.log("📡 AJAX response data:", data);

            if (data.success && data.data && data.data.locations) {
                this.locations = data.data.locations;
            } else if (data.success && Array.isArray(data.data)) {
                this.locations = data.data;
            } else {
                throw new Error(data.message || 'Invalid location data received');
            }

            console.log("📍 Processed locations:", this.locations.length, "items");
            
            if (this.locations.length === 0) {
                console.warn("⚠️ No locations found in response");
            }
        } catch (error) {
            console.error("❌ Error loading locations:", error);
            throw new Error(`Could not load location data: ${error.message}`);
        }
    }

    async initMap() {
        console.log("🗺️ Initializing map with provider...");
        
        // Initialize the map through the provider
        this.map = await this.mapProvider.initialize(this.mapElement, {
            center: this.defaultCenter,
            zoom: this.defaultZoom,
            fullscreenControl: this.mode === 'single'
        });

        // Only show country view if not in single location mode
        if (this.mode !== 'single' || !this.focusLocationId) {
            this.showCountryView();
        }
    }

    addZoomListener() {
        if (!this.allowZoomOut && this.mode === 'single') return;

        const handleZoomChange = () => {
            // Don't auto-switch if we're programmatically navigating
            if (this.isNavigating) {
                console.log("🔍 Zoom changed during navigation, ignoring auto-switch");
                return;
            }

            const currentZoom = this.mapProvider.getZoom();
            console.log("🔍 Zoom changed to:", currentZoom);

            // Close popup when zooming out in region view (before reaching country view threshold)
            if (this.currentView === "region" && currentZoom < 12) {
                this.mapProvider.closePopup();
            }

            // Zoom out: Switch to country view (show yellow region dots)
            if (
                currentZoom <= this.regionZoomThreshold &&
                this.currentView === "region"
            ) {
                console.log("🌍 Auto-switching to country view due to zoom out");
                this.showCountryView();
                this.updateControlButtons();
            }
            
            // Zoom in: Switch to region view (show stores based on zoom level)
            if (
                currentZoom > this.regionZoomThreshold &&
                this.currentView === "country"
            ) {
                console.log("🏙️ Auto-switching to show stores due to zoom in");
                this.showAllStores();
            }
            
            // Already in region view: Update markers based on zoom level
            // Zoom 9: only stores, Zoom 10+: stores + mobile stores
            if (
                currentZoom > this.regionZoomThreshold &&
                this.currentView === "region" &&
                this.currentRegion === null && // Only for "all stores" view, not specific region
                !this.userClickedStore // Don't refresh if user clicked on a specific store
            ) {
                console.log("🔄 Updating store visibility based on new zoom level");
                this.showAllStores();
            }
        };

        // Add listener based on provider type
        const provider = window.mapConfig.provider || 'google';
        if (provider === 'mapbox') {
            this.map.on('zoom', handleZoomChange);
        } else {
            this.map.addListener('zoom_changed', handleZoomChange);
        }
    }

    showCountryView() {
        console.log("🌍 Showing country view...");
        this.currentView = "country";
        this.currentRegion = null;
        this.userClickedStore = false; // Reset the flag
        this.isNavigating = true; // Disable zoom listener interference
        
        // Clear ALL markers immediately
        this.clearMarkers();
        
        // Clear any boundaries
        this.clearRegionBoundary();

        const regionLocations = this.locations.filter(
            (loc) => loc.type === "region"
        );
        console.log("📍 Region locations found:", regionLocations.length);

        // Add region markers immediately (don't wait for animation)
        regionLocations.forEach((location) => {
            this.addMarker(location, "region");
        });

        // Then do smooth transition
        this.mapProvider.flyTo(this.defaultCenter.lat, this.defaultCenter.lng, this.defaultZoom, () => {
            // Re-enable zoom listener after navigation complete
            this.isNavigating = false;
            console.log("✅ Navigation complete, zoom listener re-enabled");
        });
    }

    showAllStores() {
        console.log("🏪 Showing stores based on zoom level...");
        this.currentView = "region"; // Set to region view to show stores
        this.currentRegion = null; // No specific region, showing all
        
        // Clear region markers
        this.clearMarkers();
        
        const currentZoom = this.mapProvider.getZoom();
        console.log("🔍 Current zoom level:", currentZoom);
        
        // Zoom 9: Show only regular stores
        // Zoom 10+: Show both stores and mobile stores
        let storesToShow;
        
        if (currentZoom >= 10) {
            // Show all stores and mobile stores
            storesToShow = this.locations.filter(
                (loc) => loc.type === "store" || loc.type === "mobile_store"
            );
            console.log("📍 Zoom 10+: Showing stores + mobile stores:", storesToShow.length);
        } else {
            // Show only regular stores
            storesToShow = this.locations.filter(
                (loc) => loc.type === "store"
            );
            console.log("📍 Zoom 9: Showing only stores:", storesToShow.length);
        }
        
        // Add store markers
        storesToShow.forEach((location) => {
            this.addMarker(location, location.type);
        });
    }

    showRegionView(regionId) {
        this.currentView = "region";
        this.currentRegion = regionId;
        this.isNavigating = true; // Disable zoom listener interference
        
        // Clear ALL markers immediately
        this.clearMarkers();
        
        // Clear previous boundary
        this.clearRegionBoundary();

        const regionLocation = this.locations.find((loc) => loc.id == regionId);
        
        // Get region view mode from settings (default: all_locations)
        const regionViewMode = window.mapConfig?.regionViewMode || 'all_locations';
        
        // Get stores in THIS region (always shown immediately)
        const regionStores = this.locations.filter(
            (loc) => (loc.type === "store" || loc.type === "mobile_store") && loc.parent_region == regionId
        );
        
        // Get stores in OTHER regions (for "all_locations" mode, shown after delay)
        const otherStores = this.locations.filter(
            (loc) => (loc.type === "store" || loc.type === "mobile_store") && loc.parent_region != regionId
        );

        // Determine which stores to show immediately based on mode
        let storeLocations;
        if (this.mode === 'single') {
            // Single location pages always show only that region's stores
            storeLocations = regionStores;
        } else if (regionViewMode === 'all_locations') {
            // All Locations mode: show only THIS region's stores during animation
            // Other stores will be added after animation completes
            storeLocations = regionStores;
        } else {
            // Region Only mode: show only stores in selected region
            storeLocations = regionStores;
        }

        // Add store markers for selected region immediately
        storeLocations.forEach((location) => {
            this.addMarker(location, location.type);
        });
        
        // Add yellow region dots for OTHER regions (so user can navigate to other regions)
        // Only in full mode (not single location pages) and only in "region_only" mode
        if (this.mode === 'full' && regionViewMode === 'region_only') {
            const otherRegions = this.locations.filter((loc) => loc.type === "region" && loc.id != regionId);
            otherRegions.forEach((region) => {
                this.addMarker(region, "region");
            });
        }

        // Position map - use view_lat/view_lng for map center (separate from marker position)
        if (regionLocation) {
            // Use view coordinates if available, otherwise fall back to marker position
            const targetLat = parseFloat(regionLocation.view_lat) || parseFloat(regionLocation.lat);
            const targetLng = parseFloat(regionLocation.view_lng) || parseFloat(regionLocation.lng);
            const targetZoom = parseFloat(regionLocation.zoom_level) || 10;
            
            // For single mode, use instant positioning (no animation)
            if (this.mode === 'single') {
                this.mapProvider.setCenter(targetLat, targetLng);
                this.mapProvider.setZoom(targetZoom);
                this.isNavigating = false;
            } else {
                // Full mode: smooth animation
                this.mapProvider.flyTo(targetLat, targetLng, targetZoom, () => {
                    this.isNavigating = false;
                    
                    // In "all_locations" mode, add OTHER region stores AFTER animation
                    // No region dots needed - all stores/mobiles are visible for navigation
                    if (regionViewMode === 'all_locations') {
                        setTimeout(() => {
                            // Add stores/mobiles from other regions
                            otherStores.forEach((location) => {
                                this.addMarker(location, location.type);
                            });
                        }, 500); // 500ms delay after animation
                    }
                });
            }
        } else {
            this.isNavigating = false;
        }
    }

    showRegionBoundary(regionLocation) {
        if (!regionLocation) return;

        // Get the region name or slug to match with boundary data
        const regionKey = regionLocation.title.toLowerCase().replace(/\s+/g, '');
        const boundaryData = this.regionBoundaries.get(regionKey);

        if (!boundaryData) {
            // No boundary data - skip silently
            return;
        }

        console.log("✅ Showing boundary for:", regionKey);

        // Create subtle region boundary polygon through provider
        this.currentRegionBoundary = this.mapProvider.addPolygon(
            boundaryData.coordinates[0],
            {
                strokeColor: '#FECC20',
                strokeOpacity: 0.6,
                strokeWeight: 2,
                fillColor: '#FECC20',
                fillOpacity: 0.1
            }
        );
    }

    fadeInRegionBoundary() {
        if (!this.currentRegionBoundary) return;

        let strokeOpacity = 0;
        let fillOpacity = 0;
        const fadeStep = 0.02;
        const targetStrokeOpacity = 0.6;
        const targetFillOpacity = 0.1;

        const fadeInterval = setInterval(() => {
            strokeOpacity = Math.min(strokeOpacity + fadeStep * 3, targetStrokeOpacity);
            fillOpacity = Math.min(fillOpacity + fadeStep, targetFillOpacity);
            
            this.currentRegionBoundary.setOptions({
                strokeOpacity: strokeOpacity,
                fillOpacity: fillOpacity
            });

            if (fillOpacity >= targetFillOpacity) {
                clearInterval(fadeInterval);
            }
        }, 20);
    }

    convertGeoJSONToGoogleMaps(coordinates) {
        return coordinates.map(coord => ({
            lat: coord[1], // GeoJSON is [lng, lat], Google Maps is {lat, lng}
            lng: coord[0]
        }));
    }

    fitMapToBoundary(coordinates) {
        const bounds = new google.maps.LatLngBounds();
        coordinates.forEach(coord => {
            bounds.extend(new google.maps.LatLng(coord[1], coord[0]));
        });

        this.map.fitBounds(bounds);

        // Add some padding and ensure reasonable zoom level
        setTimeout(() => {
            const currentZoom = this.map.getZoom();
            if (currentZoom > 12) {
                this.map.setZoom(12); // Don't zoom in too much
            }
        }, 100);
    }

    clearRegionBoundary() {
        if (this.currentRegionBoundary) {
            this.mapProvider.removePolygon(this.currentRegionBoundary);
            this.currentRegionBoundary = null;
        }
    }

    fadeOutRegionBoundary(callback) {
        if (!this.currentRegionBoundary) {
            if (callback) callback();
            return;
        }

        // Just remove it immediately for now (can add fade later if needed)
        this.clearRegionBoundary();
        if (callback) callback();
    }

    smoothTransition(targetCenter, targetZoom, callback) {
        const currentCenter = this.map.getCenter();
        const currentZoom = this.map.getZoom();
        
        // Calculate differences
        const startLat = currentCenter.lat();
        const startLng = currentCenter.lng();
        const startZoom = currentZoom;
        
        const deltaLat = targetCenter.lat - startLat;
        const deltaLng = targetCenter.lng - startLng;
        const deltaZoom = targetZoom - startZoom;
        
        // Calculate distance to adjust duration
        const distance = Math.sqrt(deltaLat * deltaLat + deltaLng * deltaLng);
        const baseDuration = 800; // Base duration in milliseconds
        const duration = Math.min(baseDuration + (distance * 100), 1500); // Cap at 1.5s
        
        const steps = 60; // More steps for smoother animation
        const stepDuration = duration / steps;
        let currentStep = 0;

        // Better easing function (ease-in-out-quart)
        const easeInOutQuart = (t) => {
            return t < 0.5 
                ? 8 * t * t * t * t
                : 1 - Math.pow(-2 * t + 2, 4) / 2;
        };

        const animationInterval = setInterval(() => {
            currentStep++;
            const progress = currentStep / steps;
            const easedProgress = easeInOutQuart(progress);

            // Interpolate position and zoom
            const newLat = startLat + (deltaLat * easedProgress);
            const newLng = startLng + (deltaLng * easedProgress);
            const newZoom = startZoom + (deltaZoom * easedProgress);

            this.map.setCenter({ lat: newLat, lng: newLng });
            this.map.setZoom(newZoom);

            if (currentStep >= steps) {
                clearInterval(animationInterval);
                // Ensure we end exactly at target
                this.map.setCenter(targetCenter);
                this.map.setZoom(targetZoom);
                
                if (callback) {
                    // Small delay to ensure map is settled
                    setTimeout(callback, 150);
                }
            }
        }, stepDuration);
    }

    animateAndShowStore(location, marker) {
        console.log("🚀 Positioning map for:", location.title);
        const targetLat = parseFloat(location.lat);
        const targetLng = parseFloat(location.lng);
        const targetZoom = 16;
        
        // Calculate offset in degrees to center the popup properly
        const metersPerPixel = 156543.03392 * Math.cos(targetLat * Math.PI / 180) / Math.pow(2, targetZoom);
        const offsetPixels = 80; // Small offset so popup doesn't touch top edge
        const latOffset = (offsetPixels * metersPerPixel) / 111320; // meters to degrees
        const adjustedLat = targetLat - latOffset;

        // For single location mode, use INSTANT positioning (no animation)
        if (this.mode === 'single') {
            console.log("📍 Single mode: Using instant positioning (no animation)");
            this.mapProvider.setCenter(adjustedLat, targetLng);
            this.mapProvider.setZoom(targetZoom);
            this.isNavigating = false;
            // Show popup immediately — keep userClickedStore=true so the
            // "Region View" button knows we're zoomed into a store
            setTimeout(() => {
                console.log("✅ Showing InfoWindow.");
                this.showInfoWindow(marker, location);
            }, 100);
        } else {
            // Full mode: use smooth animation
            this.mapProvider.flyTo(adjustedLat, targetLng, targetZoom, () => {
                console.log("✅ Animation complete. Showing InfoWindow.");
                this.isNavigating = false; // Reset flag after animation
                this.showInfoWindow(marker, location);
                // Reset flag after showing popup so user can zoom freely
                setTimeout(() => {
                    this.userClickedStore = false;
                }, 500);
            });
        }
    }

    focusOnStore(location, updateSidebar = true) {
        console.log(`🎯 Focusing on store: ${location.title}`);

        // Mark that user clicked on a specific store (will be reset after zoom)
        this.userClickedStore = true;
        this.isNavigating = true; // Prevent zoom listener interference

        if (updateSidebar) {
            this.setActiveSidebarItem(location.id);
        }

        // Always clear currentRegion when focusing on a store
        // This ensures zoom listener works correctly
        const needToLoadStores = this.currentView === "country" || this.currentRegion !== null;
        
        if (needToLoadStores) {
            console.log("📍 Loading all stores for store focus");
            this.currentView = "region";
            this.currentRegion = null; // Clear region so zoom listener works properly
            this.clearMarkers();
            
            // Show all stores and mobile stores (user will zoom to specific one)
            const allStores = this.locations.filter(
                (loc) => loc.type === "store" || loc.type === "mobile_store"
            );
            console.log(`📍 Adding ${allStores.length} stores to map...`);
            allStores.forEach((loc) => {
                this.addMarker(loc, loc.type);
            });
            console.log(`✅ Added ${this.markers.length} markers to map`);
        }

        // Find marker by location ID
        const marker = this.markers.find((m) => {
            return m.locationId === location.id || (m.title === location.title);
        });
        
        if (marker) {
            console.log("✅ Found marker for:", location.title, "- animating to location");
            // Directly animate to the store - smooth, no region switching
            this.animateAndShowStore(location, marker);
        } else {
            console.error("❌ Could not find marker for:", location.title);
            console.error("Available markers:", this.markers.map(m => m.locationId || 'no-id'));
            console.error("Looking for location ID:", location.id);
            this.isNavigating = false; // Reset flag if animation won't happen
        }
    }

    addMarker(location, type) {
        console.log(
            "📌 Adding marker:",
            location.title,
            type,
            `lat: ${location.lat}, lng: ${location.lng}`
        );

        if (!location.lat || !location.lng) {
            console.warn("⚠️ Skipping location without coordinates:", location.title);
            return;
        }

        // Create marker through provider with click handler
        const marker = this.mapProvider.createMarker(location, type, () => {
            console.log("🖱️ Marker clicked:", location.title, "Type:", type, "Current view:", this.currentView);
            
            if (type === "region") {
                // Allow clicking region markers from both country view and region view
                console.log("➡️ Switching to region view:", location.id);
                this.showRegionView(location.id);
                this.setActiveSidebarItem(location.id);
                this.updateControlButtons();
            } else if (type === "store" || type === "mobile_store") {
                console.log("🏪 Store marker clicked:", location.title);
                // Set flags to prevent zoom listener interference
                this.userClickedStore = true;
                this.isNavigating = true;
                this.setActiveSidebarItem(location.id);
                this.animateAndShowStore(location, marker);
                this.updateControlButtons();
            } else {
                console.log("ℹ️ Showing info window for:", location.title);
                this.showInfoWindow(marker, location);
            }
        });

        // Store location ID for easier lookup
        marker.locationId = location.id;
        this.markers.push(marker);
    }

    createStandardMarkerIcon(type, location = null) {
        if (type === 'store') {
            return {
                url: "https://armstrong.co.nz/wp-content/uploads/2025/11/Store.png",
                scaledSize: new google.maps.Size(40, 40),
                anchor: new google.maps.Point(20, 40)
            };
        } else if (type === 'mobile_store') {
            return {
                url: "https://armstrong.co.nz/wp-content/uploads/2025/11/Truck.png",
                scaledSize: new google.maps.Size(40, 40),
                anchor: new google.maps.Point(20, 40)
            };
        } else {
            // Region marker - yellow circle
            return {
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: '#FECC20',
                fillOpacity: 1,
                strokeColor: '#fff',
                strokeWeight: 3,
                scale: 12
            };
        }
    }

    showInfoWindow(marker, location) {
        let backButton = "";
        if (this.currentView === "region" && this.showControls) {
            if (this.mode === 'single' && this.currentRegion) {
                // Single location pages: go back to region view
                const regionId = this.currentRegion;
                const mapId = this.mapElement.id;
                backButton = `<button onclick="var m=document.getElementById('${mapId}')._mapInstance; m.userClickedStore=false; m.showRegionView(${regionId}); m.updateControlButtons();" class="back-btn">← Back to Region View</button>`;
            } else {
                // Full map: go back to country view
                backButton = `<button onclick="window.mapInstance.showCountryView(); window.mapInstance.updateControlButtons();" class="back-btn">← Back to Country View</button>`;
            }
        }

        // Create image section if image exists
        const imageSection = location.image && location.image.url ? `
            <div class="info-window-image">
                <img src="${location.image.sizes.medium || location.image.url}" alt="${location.image.alt}" loading="lazy" />
            </div>
        ` : '';

        // Create action buttons
        const actionButtons = this.createActionButtons(location);
        
        // Format address with icon
        const addressSection = location.address ? `
            <div class="info-window-address">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>${location.address}</span>
            </div>
        ` : '';

        const phoneSection = location.phone_number ? `
            <div class="info-window-phone">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                <a href="tel:${location.phone_number}">${this.formatPhoneNumber(location.phone_number)}</a>
            </div>
        ` : '';

        const emailSection = location.email_address ? `
            <div class="info-window-email">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <a href="mailto:${location.email_address}">${location.email_address}</a>
            </div>
        ` : '';

        const content = `
            <div class="map-info-window">
                ${imageSection}
                <div class="info-window-content">
                    <h3 class="info-window-title">${location.title}</h3>
                    ${addressSection}
                    ${phoneSection}
                    ${emailSection}
                    ${location.info ? `<div class="info-window-description">${location.info}</div>` : ''}
                </div>
                ${actionButtons}
                ${backButton}
            </div>
        `;

        this.mapProvider.createPopup(marker, content);
    }

    createActionButtons(location) {
        const buttons = [];
        const isMobileStore = location.type === 'mobile_store';

        // For mobile stores: special layout with Phone (if exists) + Book a Job
        if (isMobileStore) {
            const hasPhone = !!location.phone_number;
            const layoutClass = hasPhone ? 'mobile-store-actions-split' : 'mobile-store-actions-centered';
            
            let mobileButtons = [];
            
            // Phone button (only if phone exists)
            if (hasPhone) {
                const formattedPhone = this.formatPhoneNumber(location.phone_number);
                mobileButtons.push(`
                    <a href="tel:${location.phone_number}" class="action-button phone-button mobile-store-btn" title="Call ${formattedPhone}">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                        </svg>
                        <span>Call</span>
                    </a>
                `);
            }
            
            // Book a Job button (always for mobile stores)
            mobileButtons.push(`
                <a href="#" class="action-button book-button book-job-button mobile-store-btn" data-breakdance-popup-reference="367" data-breakdance-popup-action="open" title="Book a job with ${location.title}">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span>Book a Job</span>
                </a>
            `);
            
            return `
                <div class="info-window-actions ${layoutClass}">
                    ${mobileButtons.join('')}
                </div>
            `;
        }

        // For regular stores: Phone, Email, Directions
        if (location.phone_number) {
            const formattedPhone = this.formatPhoneNumber(location.phone_number);
            buttons.push(`
                <a href="tel:${location.phone_number}" class="action-button phone-button" title="Call ${formattedPhone}">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    <span>Call</span>
                </a>
            `);
        }

        if (location.email_address) {
            buttons.push(`
                <a href="mailto:${location.email_address}" class="action-button email-button" title="Email ${location.email_address}">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    <span>Email</span>
                </a>
            `);
        }

        if (location.lat && location.lng) {
            const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}`;
            buttons.push(`
                <a href="${directionsUrl}" target="_blank" class="action-button directions-button" title="Get directions to ${location.title}">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polygon points="3,11 22,2 13,21 11,13 3,11"></polygon>
                    </svg>
                    <span>Directions</span>
                </a>
            `);
        }

        if (buttons.length === 0) {
            return '';
        }

        return `
            <div class="info-window-actions">
                ${buttons.join('')}
            </div>
        `;
    }

    formatPhoneNumber(phoneNumber) {
        if (!phoneNumber) return '';

        // Remove all non-digits
        const cleaned = phoneNumber.replace(/\D/g, '');

        // Format New Zealand numbers
        if (cleaned.startsWith('64')) {
            // International format
            return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
        } else if (cleaned.startsWith('0')) {
            // Local format
            return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5)}`;
        }

        return phoneNumber; // Return original if no pattern matches
    }

    createMarkerContent(type, location = null) {
        const iconElement = document.createElement("div");
        iconElement.style.cursor = "pointer";

        if (type === 'store') {
            // Use your custom Armstrong pin image
            const img = document.createElement("img");
            img.src = "https://armstrong.co.nz/wp-content/uploads/2025/07/Armstrong-location-pin.png";
            img.style.width = "40px";  // Adjust size as needed
            img.style.height = "40px"; // Adjust size as needed
            img.style.display = "block";
            img.alt = "Armstrong Store Location";

            // Handle image load errors (fallback to SVG)
            img.onerror = () => {
                iconElement.innerHTML = `
                    <svg width="32" height="32" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="8" fill="#00355B" stroke="#fff" stroke-width="2"/>
                        <circle cx="12" cy="12" r="3" fill="#fff"/>
                    </svg>
                `;
            };

            iconElement.appendChild(img);
            return iconElement;
        }

        // Keep SVG for regions
        const icons = {
            region: {
                svg: `
                    <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="16" cy="16" r="12" fill="#FECC20" stroke="#fff" stroke-width="3"/>
                        <circle cx="16" cy="16" r="4" fill="#fff"/>
                    </svg>
                `,
            }
        };

        iconElement.innerHTML = icons[type]?.svg || icons["region"].svg;
        return iconElement;
    }

    addControls() {
        const controlDiv = document.createElement("div");
        controlDiv.className = "map-controls armstrong-map-controls";
        controlDiv.style.cssText = "position: absolute; top: 10px; left: 10px; z-index: 5; background: transparent; border: none; box-shadow: none; width: auto; height: auto; padding: 0; margin: 0;";

        if (this.mode === 'single') {
            // Single location pages: contextual back button
            // "Region View" when zoomed into a store, "Country View" when at region level
            const backBtn = document.createElement("button");
            backBtn.className = "map-control-btn armstrong-map-back-btn";
            backBtn.style.cssText = "width: auto; height: auto;"; // Override Mapbox button styles
            backBtn.addEventListener("click", () => {
                if (this.userClickedStore) {
                    // Zoomed into a store — go back to region view
                    this.userClickedStore = false;
                    if (this.currentRegion) {
                        this.showRegionView(this.currentRegion);
                    } else if (this.focusLocationId) {
                        this.focusOnSingleLocation();
                    }
                } else if (this.currentView === "region") {
                    // At region level — go to country view
                    this.showCountryView();
                }
                this.updateControlButtons();
            });
            this.singleBackBtn = backBtn; // Store reference (no ID — multiple instances possible)
            controlDiv.appendChild(backBtn);

            // Hide entire control div initially (shown by updateControlButtons)
            controlDiv.style.display = "none";
        } else {
            // Full map: always "Country View"
            const countryBtn = document.createElement("button");
            countryBtn.id = "country-view-btn";
            countryBtn.className = `map-control-btn ${this.currentView === "country" ? "active" : ""}`;
            countryBtn.textContent = "Country View";
            countryBtn.addEventListener("click", () => {
                this.showCountryView();
                this.updateControlButtons();
            });
            controlDiv.appendChild(countryBtn);
        }

        // Store reference for easy access
        this.controlDiv = controlDiv;
        this.mapElement.appendChild(controlDiv);
    }

    updateControlButtons() {
        if (this.mode === 'single') {
            if (!this.singleBackBtn || !this.controlDiv) return;

            if (this.userClickedStore) {
                // Zoomed into a store — show "Region View"
                this.singleBackBtn.textContent = "Region View";
                this.controlDiv.style.display = "";
            } else if (this.currentView === "region") {
                // At region level — show "Country View"
                this.singleBackBtn.textContent = "Country View";
                this.controlDiv.style.display = "";
            } else {
                // At country level — hide entirely
                this.controlDiv.style.display = "none";
            }
        } else {
            const countryBtn = document.getElementById("country-view-btn");
            if (countryBtn) {
                countryBtn.className = `map-control-btn ${this.currentView === "country" ? "active" : ""}`;
            }
        }
    }

    clearMarkers() {
        this.mapProvider.clearMarkers();
        this.markers = [];
        console.log("🗑️ All markers cleared");
    }
}

// Initialize full map with sidebar
function initializeMap() {
    console.log("🔍 initializeMap() called");
    const mapElement = document.getElementById("interactive-map");

    if (mapElement) {
        console.log("✅ Initializing InteractiveMap...");
        window.mapInstance = new InteractiveMap(mapElement, { mode: 'full' });
    }
}

// Initialize single location maps
function initializeSingleMaps() {
    if (!window.singleMapConfigs || window.singleMapConfigs.length === 0) return;

    console.log('🗺️ Initializing single location maps');

    window.singleMapConfigs.forEach(config => {
        const mapElement = document.getElementById(config.mapId);
        if (mapElement) {
            new InteractiveMap(mapElement, {
                mode: 'single',
                focusLocationId: config.locationId,
                showControls: config.showControls,
                allowZoomOut: config.allowZoomOut
            });
        }
    });

    // Clear configs after initialization
    window.singleMapConfigs = [];
}

// Initialize on DOM ready
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
        initializeMap();
        initializeSingleMaps();
    });
} else {
    initializeMap();
    initializeSingleMaps();
}

// Also check on window load
window.addEventListener("load", function () {
    if (!window.mapInstance) {
        initializeMap();
    }
    initializeSingleMaps();
});

// Export class globally
window.InteractiveMap = InteractiveMap;

})(); // End WordPress wrapper
